import {
  EditNodeTemplate,
  IEditNodeTemplateOptions,
} from '@/components/edit-node-template';
import {
  Appender,
  EditChildren,
  NodeContainer,
  NodeDialogProvider,
} from '@/node-editor/components';
import {
  getChildren,
  getRootNode,
  useSelectors,
} from '@/node-editor/store/selectors';
import {
  filterSavedTemplateData,
  fromJsonTemplate,
} from '@/node-editor/utilities';
import { addNodeTemplateService } from '@/services/nodeTemplateService';
import { INodeTemplate } from '@/types/node';
import { WIDGET_AREAS } from '@/widget-editor/utilities';
import { Icon, IconButton } from '@mui/material';
import React, { useRef, useMemo, useCallback, memo } from 'react';
import { FieldValues } from 'react-hook-form';
import { reorderNode, useActions } from '@/node-editor/store/actions';
import { Theme, useTheme } from '@mui/material/styles';

const useStyles = (theme: Theme) =>
  useMemo(
    () => ({
      root: {
        padding: `${theme.spacing(1)}px !important`,
        background: `${theme.palette.grey[300]} !important`,
      },
      saveTemplate: {
        opacity: '0.3 !important',
        '&:hover': {
          opacity: '1 !important',
        },
      },
    }),
    [theme]
  );

export const Edit = memo(({ model }: { model: 'page' | 'room' }) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const actions = useActions(() => ({
    reorderNode,
  }));

  const selectors = useSelectors(() => ({
    getRootNode,
    getChildren,
  }));

  const templateRef = useRef<{
    open: (initOptions: IEditNodeTemplateOptions) => void;
  } | null>(null);

  // Memoize root node to prevent unnecessary re-renders
  const root = useMemo(() => selectors.getRootNode(), [selectors]);

  // Memoize children calculation
  const children = useMemo(() => {
    if (!root) return [];
    return selectors.getChildren(root.id);
  }, [selectors, root?.id]);

  // Memoize widget area count calculation
  const widgetAreaCount = useMemo(() => {
    if (!root) return 0;
    return children.filter((node) => node.type === 'widget-area').length;
  }, [children]);

  const hideAddToRoot = useMemo(
    () => model === 'room' && widgetAreaCount >= WIDGET_AREAS.length,
    [model, widgetAreaCount]
  );

  const handleSaveAsRootTemplate = useCallback(
    (
      { title, description, picture, public: showPublic }: FieldValues,
      conferenceId: string
    ) => {
      if (!root) return;

      const nodeTemplate: Partial<INodeTemplate[]> = [
        ({
          ...root,
          children: children,
        } as unknown) as INodeTemplate,
      ] as Partial<INodeTemplate[]>;
      const nodeList = filterSavedTemplateData(
        fromJsonTemplate(nodeTemplate),
        conferenceId
      );

      addNodeTemplateService({
        title,
        description,
        picture,
        public: showPublic,
        type: 'root',
        nodes: nodeList,
      });
    },
    [root, children]
  );

  const openSaveAsTemplate = useCallback(() => {
    templateRef.current?.open({
      saveAsTemplate: handleSaveAsRootTemplate,
    });
  }, [handleSaveAsRootTemplate]);

  // Memoize the onDrop handler to prevent unnecessary re-renders
  const handleDrop = useCallback(
    ({ source, destination }: { source: any; destination: any }) => {
      if (!root) return;
      actions.reorderNode(root.id, source.index, destination.index);
    },
    [actions, root?.id]
  );

  return (
    root && (
      <NodeDialogProvider supports={model}>
        <NodeContainer
          id={root.id}
          sx={{ ...classes.root }}
          onDrop={handleDrop}
        >
          <EditChildren>{children}</EditChildren>
          {!hideAddToRoot && <Appender nodeId={root.id} />}
          {!hideAddToRoot && (
            <IconButton
              onClick={openSaveAsTemplate}
              size="small"
              sx={classes.saveTemplate}
            >
              <Icon>bookmark</Icon>
            </IconButton>
          )}

          <EditNodeTemplate ref={templateRef} />
        </NodeContainer>
      </NodeDialogProvider>
    )
  );
});
